import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { useMergeRefs } from '@floating-ui/react';

import { Comp, getContainerStylingTransientProps, useBoundingClientRect } from '@redsift/design-system';
import { StyledDialogContentBody } from './styles';
import { DialogContentBodyProps } from './types';
import { useDialogContext } from '../dialog';

const COMPONENT_NAME = 'DialogContentBody';
const CLASSNAME = 'redsift-dialog-content-body';

/**
 * The DialogContentBody component.
 */
export const DialogContentBody: Comp<DialogContentBodyProps, HTMLDivElement> = forwardRef((props, ref) => {
  const { transientProps, otherProps } = getContainerStylingTransientProps(props);

  const { children, className, ...forwardedProps } = otherProps;

  const { actionsRef, bodyRef, hasCloseButton, headerRef, initialFocus } = useDialogContext();

  const { height: headerHeight = 0 } = useBoundingClientRect(headerRef as any, [children, headerRef]);
  const { height: actionsHeight = 0 } = useBoundingClientRect(actionsRef as any, [children, actionsRef]);

  /* istanbul ignore next */
  const marginTop = Math.max(headerHeight, hasCloseButton ? 40 : 0) + 32;
  const marginBottom = (actionsHeight === 0 ? 0 : actionsHeight + 16) + 32;
  const maxHeight = `calc(100vh - ${marginTop + marginBottom}px)`;

  return (
    <StyledDialogContentBody
      {...forwardedProps}
      {...transientProps}
      className={classNames(DialogContentBody.className, className)}
      ref={useMergeRefs([ref, bodyRef])}
      tabIndex={initialFocus === 'body' ? -1 : undefined}
      $marginTop={`${marginTop}px`}
      $marginBottom={`${marginBottom}px`}
      $maxHeight={maxHeight}
    >
      {children}
    </StyledDialogContentBody>
  );
});
DialogContentBody.className = CLASSNAME;
DialogContentBody.displayName = COMPONENT_NAME;
